import {useEffect} from 'react';

import {isNil, isNotEmpty, isNotNil, keys} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistsQuery} from '@dms/api/core';

type ResourceId = 'sales' | 'rental' | 'workshop' | 'customer';
type CodeListId = string;

const SORTED_LISTS: Record<CodeListId, ResourceId> = {
  // rental
  cancellationReason: 'rental',
  carPark: 'rental',
  equipment: 'rental',
  rentType: 'rental',
  vehicleBlock: 'rental',
  vehicleCategory: 'rental',
  // sales
  leasing_and_credit_company: 'sales',
  unsuccessful_business_case_reason: 'sales',
  unsuccessful_interest_reason: 'sales',
  interest_source: 'sales',
  vehicle_source: 'sales',
  payment_preference: 'sales',
  // workshop - managed in Settings -> Workshop -> Interest, not listed on the Codelists page
  workshop_interest_source: 'workshop',
  // customer - managed in Settings -> Customer, not listed on the Codelists page
  customer_group: 'customer',
};

export const useCodeLists = () => {
  const {data: codeList, ...queryStatus} = useReadCodelistsQuery({});

  useEffect(
    function warnAboutUnmappedCodeLists() {
      if (isNilOrEmpty(codeList)) {
        return;
      }

      const allCodeListIds = keys(SORTED_LISTS);
      const missingCodeListIds = codeList?.filter(
        (item) => !allCodeListIds.includes(item.codelistId)
      );

      // NOTE: we want to sort codeLists by resource (business requirement), this cannot be done on BE, since it's not aware of the resource
      // so we need to do it on FE, and we need to make sure that all code lists are present
      // -----------------------------------
      // if you encounter this error, please add the missing code list to the SORTED_LISTS object
      if (isNotNil(missingCodeListIds) && isNotEmpty(missingCodeListIds)) {
        console.warn(
          `New code lists returned from BE are not mapped in SORTED_LISTS: ${missingCodeListIds.map((item) => item.codelistId).join(', ')}. Add them to SORTED_LISTS in useCodeLists.ts with the correct ResourceId.`
        );
      }
    },
    [codeList]
  );

  const sortedCodeLists = codeList?.reduce(
    (acc, item) => {
      const resource = SORTED_LISTS[item.codelistId];

      if (isNil(resource)) {
        return acc;
      }

      const assignedItems = acc[resource];

      if (isNotNil(assignedItems)) {
        acc[resource].push(item);
      } else {
        acc[resource] = [item];
      }

      return acc;
    },
    {} as Record<ResourceId, {codelistId: string; name: string}[]>
  );

  return {data: sortedCodeLists, ...queryStatus};
};
